import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { CasinoCard } from "@/components/CasinoCard";
import { RiskNotice } from "@/components/RiskNotice";
import { Hero } from "@/components/Hero";
import { InternalLinks } from "@/components/InternalLinks";
import { listCasinos } from "@/data/casinos";
import { paymentMethods } from "@/data/payments";
import { providers } from "@/data/providers";
import { guides } from "@/data/guides";
import {
  casinoReviewPath,
  guidePath,
  paymentPath,
  providerPath,
} from "@/lib/format";

type Tab = "licensed" | "unlicensed" | "swish" | "bonus" | "new";

const TAB_FILTER: Record<Tab, () => Parameters<typeof listCasinos>[0]> = {
  licensed: () => ({ swedishLicense: true }),
  unlicensed: () => ({ noSwedishLicense: true }),
  swish: () => ({ swish: true }),
  bonus: () => ({}),
  new: () => ({}),
};

export function HomePage() {
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const [tab, setTab] = useState<Tab>("licensed");

  const filtered = useMemo(() => {
    return listCasinos(TAB_FILTER[tab]()).sort((a, b) => {
      if (a.priority_brand !== b.priority_brand)
        return Number(b.priority_brand) - Number(a.priority_brand);
      return b.overall_rating - a.overall_rating;
    });
  }, [tab]);

  const tabs: { key: Tab; label: string }[] = [
    { key: "licensed", label: t("homepage.tab.licensed") },
    { key: "unlicensed", label: t("homepage.tab.unlicensed") },
    { key: "swish", label: t("homepage.tab.swish") },
    { key: "bonus", label: t("homepage.tab.bonus") },
    { key: "new", label: t("homepage.tab.new") },
  ];

  const featuredGuides = guides.slice(0, 6);
  const topPayments = paymentMethods
    .filter((p) => p.available_in_sweden)
    .slice(0, 8);
  const topProviders = providers.slice(0, 6);
  const latestReviews = listCasinos().slice(0, 6);

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: filtered.slice(0, 10).map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://casinomenu.se${casinoReviewPath(prefix, lang, c.slug)}`,
      name: c.name,
    })),
  };

  return (
    <>
      <SEO
        title={
          isSv
            ? "CasinoMenu.se – Jämför casinon i Sverige"
            : "CasinoMenu.se – Compare Swedish casinos"
        }
        description={
          isSv
            ? "Jämför svenska casinon, casinon utan svensk licens, bonusar, betalningsmetoder och spel — på en plats."
            : "Compare Swedish casinos, casinos without Swedish license, bonuses, payment methods and games — in one place."
        }
        path={prefix}
        jsonLd={itemListSchema}
      />

      <Hero />

      {/* Toplist */}
      <section className="container-page mt-14">
        <div className="section-head">
          <div>
            <span className="eyebrow">{isSv ? "Topplista" : "Top list"}</span>
            <h2 className="mt-2 text-3xl font-bold">
              {t("site.compareCasinos")}
            </h2>
            <p className="mt-1 text-ink-600">
              {isSv
                ? "Vi jämför licens, betalningar, bonus och säkerhet."
                : "We compare license, payments, bonus and safety."}
            </p>
          </div>
          <Link
            to={`${prefix}/casino`}
            className="text-sm font-semibold text-brand-700 no-underline hover:text-brand-800"
          >
            {t("homepage.allCasinos")} →
          </Link>
        </div>

        <div className="mt-5 flex flex-wrap gap-2 overflow-x-auto rounded-xl border border-ink-100 bg-white p-1 shadow-card">
          {tabs.map((tb) => (
            <button
              key={tb.key}
              type="button"
              onClick={() => setTab(tb.key)}
              aria-pressed={tab === tb.key}
              className={`rounded-lg px-3.5 py-2 text-sm font-semibold transition ${
                tab === tb.key
                  ? "bg-brand-700 text-white shadow-sm"
                  : "text-ink-700 hover:bg-ink-100"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {tab === "unlicensed" && (
          <div className="mt-3">
            <RiskNotice variant="unlicensed" />
          </div>
        )}

        <div className="mt-5 grid gap-4">
          {filtered.length === 0 ? (
            <div className="card p-8 text-center text-ink-500">
              {t("list.empty")}
            </div>
          ) : (
            filtered.slice(0, 8).map((c, i) => (
              <CasinoCard key={c.id} casino={c} rank={i + 1} source="homepage" />
            ))
          )}
        </div>
      </section>

      {/* Why CasinoMenu */}
      <section className="container-page mt-20">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              icon: "✓",
              title: isSv ? "Svensk licens i fokus" : "Swedish license focus",
              body: isSv
                ? "Vi prioriterar casinon med licens från Spelinspektionen och visar tydligt vilka som är anslutna till Spelpaus."
                : "We prioritise casinos licensed by Spelinspektionen and clearly mark which are connected to Spelpaus.",
            },
            {
              icon: "★",
              title: isSv ? "Egen bedömning" : "Independent rating",
              body: isSv
                ? "Vår betygsmodell baseras på säkerhet, betalningar, bonus, spel, mobil och support – ingen kopierad ranking."
                : "Our rating model is based on safety, payments, bonus, games, mobile and support — no copied rankings.",
            },
            {
              icon: "↗",
              title: isSv ? "Tvåspråkigt" : "Bilingual",
              body: isSv
                ? "Allt på svenska först, med engelsk översättning. Smart hreflang och språkväxlare på varje sida."
                : "Swedish first, with English alongside. Smart hreflang and a language switcher on every page.",
            },
          ].map((f) => (
            <div key={f.title} className="card p-6">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand-50 text-lg font-bold text-brand-800">
                {f.icon}
              </span>
              <h3 className="mt-3 text-lg font-semibold">{f.title}</h3>
              <p className="mt-1 text-sm text-ink-600">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Guides */}
      <section className="container-page mt-20">
        <div className="section-head">
          <div>
            <span className="eyebrow">
              {isSv ? "Lär dig först" : "Learn first"}
            </span>
            <h2 className="mt-2 text-3xl font-bold">
              {t("homepage.popularGuides")}
            </h2>
          </div>
          <Link
            to={`${prefix}/${isSv ? "guider" : "guides"}`}
            className="text-sm font-semibold text-brand-700 no-underline hover:text-brand-800"
          >
            {t("cta.seeAll")} →
          </Link>
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {featuredGuides.map((g) => (
            <Link
              key={g.id}
              to={guidePath(prefix, lang, isSv ? g.slug_sv : g.slug_en)}
              className="card-interactive group flex flex-col p-5 no-underline"
            >
              <span className="text-[11px] font-semibold uppercase tracking-widest text-brand-700">
                {g.category}
              </span>
              <h3 className="mt-2 text-lg font-semibold text-ink-900 group-hover:text-brand-700">
                {isSv ? g.title_sv : g.title_en}
              </h3>
              <p className="mt-2 line-clamp-3 text-sm text-ink-600">
                {isSv ? g.excerpt_sv : g.excerpt_en}
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-700">
                {isSv ? "Läs guide" : "Read guide"} →
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Payments + Providers */}
      <section className="container-page mt-20 grid gap-10 lg:grid-cols-2">
        <div>
          <div className="section-head">
            <div>
              <span className="eyebrow">{isSv ? "Snabba uttag" : "Fast payouts"}</span>
              <h2 className="mt-2 text-3xl font-bold">
                {t("homepage.topPayments")}
              </h2>
            </div>
            <Link
              to={`${prefix}/${isSv ? "betalningsmetoder" : "payment-methods"}`}
              className="text-sm font-semibold text-brand-700 no-underline hover:text-brand-800"
            >
              {t("cta.seeAll")} →
            </Link>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {topPayments.map((p) => (
              <Link
                key={p.id}
                to={paymentPath(prefix, lang, isSv ? p.slug_sv : p.slug_en)}
                className="card-interactive flex items-center justify-between p-3 text-sm font-semibold text-ink-800 no-underline"
              >
                <span>{p.name}</span>
                <span aria-hidden className="text-brand-500">
                  →
                </span>
              </Link>
            ))}
          </div>
        </div>
        <div>
          <div className="section-head">
            <div>
              <span className="eyebrow">{isSv ? "Spelutbud" : "Game library"}</span>
              <h2 className="mt-2 text-3xl font-bold">
                {t("homepage.topProviders")}
              </h2>
            </div>
            <Link
              to={`${prefix}/${isSv ? "speltillverkare" : "software-providers"}`}
              className="text-sm font-semibold text-brand-700 no-underline hover:text-brand-800"
            >
              {t("cta.seeAll")} →
            </Link>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {topProviders.map((p) => (
              <Link
                key={p.id}
                to={providerPath(prefix, lang, p.slug)}
                className="card-interactive flex items-center justify-between p-3 text-sm font-semibold text-ink-800 no-underline"
              >
                <span>{p.name}</span>
                <span aria-hidden className="text-brand-500">
                  →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest reviews */}
      <section className="container-page mt-20">
        <div className="section-head">
          <div>
            <span className="eyebrow">
              {isSv ? "Recensioner" : "Reviews"}
            </span>
            <h2 className="mt-2 text-3xl font-bold">
              {t("homepage.latestReviews")}
            </h2>
          </div>
          <Link
            to={`${prefix}/casino`}
            className="text-sm font-semibold text-brand-700 no-underline hover:text-brand-800"
          >
            {t("cta.seeAll")} →
          </Link>
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {latestReviews.map((c) => (
            <Link
              key={c.id}
              to={casinoReviewPath(prefix, lang, c.slug)}
              className="card-interactive group p-5 no-underline"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-ink-900 group-hover:text-brand-700">
                  {c.name}
                </h3>
                {c.priority_brand && (
                  <span className="pill-gold">★ {t("label.priorityBrand")}</span>
                )}
              </div>
              <p className="mt-2 line-clamp-2 text-sm text-ink-600">
                {isSv ? c.intro_sv : c.intro_en}
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {c.swedish_license ? (
                  <span className="pill-success">{t("label.swedishLicense")}</span>
                ) : (
                  <span className="pill-warn">{t("label.noSwedishLicense")}</span>
                )}
                {c.swish_supported && <span className="pill-brand">Swish</span>}
                {c.crypto_supported && <span className="pill-brand">Crypto</span>}
              </div>
              <span className="mt-4 inline-flex items-center text-sm font-semibold text-brand-700">
                {t("cta.readReview")} →
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Internal linking */}
      <div className="container-page">
        <InternalLinks context="homepage" />
      </div>

      {/* Responsible block */}
      <section className="container-page my-20">
        <div className="grid gap-4 lg:grid-cols-2">
          <RiskNotice variant="general" />
          <RiskNotice variant="spelpaus" />
        </div>
      </section>
    </>
  );
}
